import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPublicSetting, addToWaitlist } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import './Auth.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const TOS_VERSION = '1.0';

const getStoredRef = (): string | null => {
    try {
        const raw = localStorage.getItem('aime_ref');
        if (!raw) return null;
        const { code, expiry } = JSON.parse(raw);
        if (Date.now() > expiry) {
            localStorage.removeItem('aime_ref');
            return null;
        }
        return code;
    } catch {
        return null;
    }
};

const RegisterPage: React.FC = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
    });
    const [tosAgreed, setTosAgreed] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [checkingRegistration, setCheckingRegistration] = useState(true);
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [waitlistSuccess, setWaitlistSuccess] = useState(false);

    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Pre-fill email from URL params (e.g. from homepage assessment quiz)
    useEffect(() => {
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setFormData(prev => ({ ...prev, email: emailParam }));
            setWaitlistEmail(emailParam);
        }
    }, [searchParams]);

    useEffect(() => {
        const checkRegistration = async () => {
            try {
                const setting = await getPublicSetting('registration_enabled');
                if (setting.value.toLowerCase() !== 'true') {
                    setShowWaitlist(true);
                }
            } catch {
                // If the setting can't be fetched, allow registration by default
            } finally {
                setCheckingRegistration(false);
            }
        };
        checkRegistration();
    }, [navigate]);

    const handleChange = (name: string, value: string) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleWaitlistSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!waitlistEmail) return;

        setError('');
        setIsLoading(true);

        try {
            await addToWaitlist(waitlistEmail);
            setWaitlistSuccess(true);
        } catch (err: any) {
            console.error('Waitlist error:', err);
            setError(getApiError(err, 'Failed to join waitlist. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (!tosAgreed) {
            setError('You must agree to the Terms of Service and Privacy Policy to create an account');
            return;
        }

        setIsLoading(true);

        try {
            const refCode = getStoredRef();
            let detectedTz: string | undefined;
            try {
                detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
            } catch {
                detectedTz = undefined;
            }
            const { checkout_url } = await register({
                username: formData.username,
                email: formData.email,
                password: formData.password,
                full_name: formData.fullName || undefined,
                tos_version: TOS_VERSION,
                ...(refCode ? { referral_code: refCode } : {}),
                ...(detectedTz ? { timezone: detectedTz } : {}),
            });
            localStorage.removeItem('aime_ref');

            if (checkout_url) {
                // Phase 2: $0.99 trial — redirect to Stripe Checkout
                window.location.href = checkout_url;
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (err: any) {
            console.error('Registration error:', err);
            setError(getApiError(err, 'Failed to create account. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    if (checkingRegistration) {
        return (
            <div className="auth-page">
                <GlassCard variant="strong" padding="lg" className="auth-container">
                    <div className="auth-header">
                        <h1>AiMe</h1>
                    </div>
                    <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</p>
                </GlassCard>
            </div>
        );
    }

    if (showWaitlist) {
        return (
            <div className="auth-page">
                <GlassCard variant="strong" padding="lg" className="auth-container">
                    <div className="auth-header">
                        <h1>AiMe</h1>
                        <h2>Join the Waitlist</h2>
                    </div>

                    {waitlistSuccess ? (
                        <div className="waitlist-success-container" style={{ textAlign: 'center', padding: '1rem 0' }}>
                            <div className="waitlist-success-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
                            <p className="waitlist-success-msg" style={{ marginBottom: '2rem', lineHeight: '1.5' }}>
                                You've been added to the waitlist! We'll email you when more spots on Node 01 become available.
                            </p>
                            
                            <div className="waitlist-fast-pass" style={{ 
                                background: 'rgba(255, 255, 255, 0.05)', 
                                padding: '1.5rem', 
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                marginBottom: '2rem'
                            }}>
                                <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Want to skip the line?</h3>
                                <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Get a Priority Spot for $9</p>
                                <GlassButton 
                                    onClick={() => window.open('https://buy.stripe.com/4gM5kD4uG2TQ5swbn77Vm00', '_blank')}
                                    variant="primary"
                                    className="priority-btn"
                                    style={{ width: '100%' }}
                                >
                                    Buy Priority Spot
                                </GlassButton>
                            </div>
                            
                            <div className="auth-footer">
                                <Link to="/login">Back to Sign in</Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleWaitlistSubmit} className="auth-form">
                            {error && (
                                <div className="auth-error">
                                    {error}
                                </div>
                            )}
                            
                            <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', textAlign: 'center', fontSize: '0.95rem' }}>
                                Node 01 registration is currently restricted. Join the waitlist to be notified when a spot opens up.
                            </p>
                            
                            <GlassInput
                                label="Email"
                                type="email"
                                value={waitlistEmail}
                                onChange={(value) => setWaitlistEmail(value)}
                                placeholder="Enter your email"
                                disabled={isLoading}
                                autoFocus
                            />
                            
                            <GlassButton
                                type="submit"
                                variant="primary"
                                size="lg"
                                loading={isLoading}
                                disabled={isLoading || !waitlistEmail}
                                className="auth-submit-btn"
                            >
                                Join Waitlist
                            </GlassButton>
                            
                            <div className="waitlist-fast-pass-inline" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                    Want to skip the line?{' '}
                                    <a 
                                        href="https://buy.stripe.com/4gM5kD4uG2TQ5swbn77Vm00" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: 'var(--primary-light)', fontWeight: 'bold', textDecoration: 'none' }}
                                    >
                                        Get a Priority Spot for $9
                                    </a>
                                </p>
                            </div>
                        </form>
                    )}

                    {!waitlistSuccess && (
                        <div className="auth-footer">
                            <p>
                                Already have an account?{' '}
                                <Link to="/login">Sign in</Link>
                            </p>
                        </div>
                    )}
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <GlassCard variant="strong" padding="lg" className="auth-container">
                <div className="auth-header">
                    <h1>AiMe</h1>
                    <h2>Create Account</h2>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="auth-error">
                            {error}
                        </div>
                    )}

                    <GlassInput
                        label="Username"
                        value={formData.username}
                        onChange={(value) => handleChange('username', value)}
                        placeholder="Choose a username"
                        autoFocus
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(value) => handleChange('email', value)}
                        placeholder="Enter your email"
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Full Name (optional)"
                        value={formData.fullName}
                        onChange={(value) => handleChange('fullName', value)}
                        placeholder="Enter your full name"
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Password"
                        type="password"
                        value={formData.password}
                        onChange={(value) => handleChange('password', value)}
                        placeholder="Create a password (min 6 characters)"
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Confirm Password"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(value) => handleChange('confirmPassword', value)}
                        placeholder="Confirm your password"
                        disabled={isLoading}
                    />

                    <label className="auth-tos-checkbox">
                        <input
                            type="checkbox"
                            checked={tosAgreed}
                            onChange={(e) => setTosAgreed(e.target.checked)}
                            disabled={isLoading}
                        />
                        <span>
                            I agree to the{' '}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer">
                                Terms of Service
                            </Link>
                            {' '}and{' '}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                                Privacy Policy
                            </Link>
                        </span>
                    </label>

                    <GlassButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        loading={isLoading}
                        disabled={isLoading || !tosAgreed}
                        className="auth-submit-btn"
                    >
                        Create Account
                    </GlassButton>
                </form>

                <div className="auth-footer">
                    <p>
                        Already have an account?{' '}
                        <Link to="/login">Sign in</Link>
                    </p>
                </div>

                <p style={{ textAlign: 'center', marginTop: '12px' }}>
                    <Link to="/support" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Need help?</Link>
                </p>
            </GlassCard>
        </div>
    );
};

export default RegisterPage;
